import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../../styles/ContactSection.css';

const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        inquiry: 'demo',
        message: ''
    });

    const [submitStatus, setSubmitStatus] = useState({
        submitted: false,
        success: false,
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [gdprConsent, setGdprConsent] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const recaptchaRef = useRef();
    const [widgetId, setWidgetId] = useState(null);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const renderRecaptcha = useCallback(() => {
        if (!recaptchaRef.current || widgetId !== null) return;
        
        try {
            // Clear container
            recaptchaRef.current.innerHTML = '';
            
            const id = window.grecaptcha.enterprise.render(recaptchaRef.current, {
                sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                callback: (token) => setRecaptchaToken(token),
                'expired-callback': () => setRecaptchaToken(null),
                'error-callback': () => setRecaptchaToken(null)
            });
            
            setWidgetId(id);
        } catch (error) {
            console.error('reCAPTCHA render error:', error);
        }
    }, [widgetId]);

    useEffect(() => {
        let isMounted = true;
        
        const initRecaptcha = () => {
            if (!isMounted) return;
            
            // Clean up any existing scripts
            const existingScripts = document.querySelectorAll('script[src*="recaptcha"]');
            existingScripts.forEach(script => {
                if (script.src.includes('enterprise.js')) {
                    script.remove();
                }
            });
            
            // Clear existing grecaptcha
            if (window.grecaptcha) {
                delete window.grecaptcha;
            }
            if (window.___grecaptcha_cfg) {
                delete window.___grecaptcha_cfg;
            }
            
            // Load fresh Enterprise reCAPTCHA
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = `https://www.google.com/recaptcha/enterprise.js?hl=en&render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
            
            script.onload = () => {
                if (!isMounted) return;
                
                if (window.grecaptcha && window.grecaptcha.enterprise) {
                    window.grecaptcha.enterprise.ready(() => {
                        if (!isMounted || widgetId !== null) return;
                        renderRecaptcha();
                    });
                }
            };
            
            script.onerror = () => {
                console.error('Failed to load reCAPTCHA script');
            };
            
            document.head.appendChild(script);
        };
        
        // Small delay to ensure component is fully mounted
        const timer = setTimeout(initRecaptcha, 100);
        
        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [widgetId, renderRecaptcha]);

    const resetRecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise && widgetId !== null) {
            try {
                window.grecaptcha.enterprise.reset(widgetId);
                setRecaptchaToken(null);
            } catch (error) {
                console.error('reCAPTCHA reset error:', error);
            }
        }
    };

    const handleButtonClick = (e) => {
        if (!gdprConsent) {
            e.preventDefault();
            setErrors({ gdpr: 'You must agree to the privacy policy to continue' });
            return;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        
        // Check if reCAPTCHA is completed
        if (!recaptchaToken) {
            setErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
            return;
        }
        
        // Check if GDPR consent is given
        if (!gdprConsent) {
            setErrors({ gdpr: 'You must agree to the privacy policy to continue' });
            return;
        }
        
        setIsSubmitting(true);
        setErrors({});
        
        try {
            // Simulate API call
            const form = document.getElementsByClassName('contact-form')[0];
            const fd = new FormData(form);
            const body = Object.fromEntries(fd.entries());
            await fetch(
                "https://contact-form-1046990618188.us-west1.run.app/api/contact",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );
            
            setSubmitStatus({
                submitted: true,
                success: true,
                message: 'Thank you for your inquiry! We will get back to you within one business day.'
            });
            
            setFormData({
                name: '',
                email: '',
                company: '',
                inquiry: 'demo',
                message: ''
            });
            
            setGdprConsent(false);
            setMarketingConsent(false);
            
            // Reset reCAPTCHA
            resetRecaptcha();
            
            setTimeout(() => {
                setSubmitStatus({ submitted: false, success: false, message: '' });
            }, 5000);
            
        } catch (error) {
            setSubmitStatus({
                submitted: true,
                success: false,
                message: 'Sorry, there was an error sending your message. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inquiryTypes = [
        { value: 'other', label: 'General Inquiry' },
        { value: 'demo', label: 'Request a Platform Demo' },
        // { value: 'trial', label: 'Free Trial Access' },
        { value: 'consulting', label: 'Steel Development Consulting' },
        { value: 'partnership', label: 'Partnership Opportunities' }
    ];

    return (
        <section className="contact-section" id="contact">
            <div className="contact-background-gradient"></div>
            <div className="value-container">
                <div className="section-header">
                    <span className="section-label">Get in Touch</span>
                    <h2 className="section-title">Let's Accelerate Your Steel Innovation Together</h2>
                    <p className="section-subtitle">
                        Ready to transform your steel development process? Whether you're interested in a personalized demo, 
                        exploring consulting opportunities, or need technical support, our experts are here to help you 
                        unlock the full potential of advanced steel innovation.
                    </p>
                </div>
                
                <div className="contact-grid">
                    <div className="contact-form-container">
                        <div className="form-header">
                            <h3 className="subsection-title">Start Your Journey</h3>
                            <p className="section-subtitle">Tell us about your project and let's discuss how we can help</p>
                        </div>
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={errors.name ? 'error' : ''}
                                        placeholder="Your full name"
                                        required
                                    />
                                    {errors.name && <span className="error-message">{errors.name}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="email">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'error' : ''}
                                        placeholder="your.email@company.com"
                                        required
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="company">
                                        Company
                                    </label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        placeholder="Your company name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="inquiry">
                                        How can we help?
                                    </label>
                                    <select
                                        id="inquiry"
                                        name="inquiry"
                                        value={formData.inquiry}
                                        onChange={handleChange}
                                    >
                                        {inquiryTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="message">
                                    Project Details
                                    <span className="label-optional">(Optional)</span>
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="6"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Tell us about your project, timeline, specific challenges, or any questions you have about our platform..."
                                ></textarea>
                            </div>
                            
                            <div className="gdpr-compliance">
                                <div className="gdpr-checkboxes">
                                    <label className="gdpr-checkbox">
                                        <input
                                            type="checkbox"
                                            name="gdprConsent"
                                            checked={gdprConsent}
                                            onChange={(e) => {
                                                setGdprConsent(e.target.checked);
                                                if (errors.gdpr) {
                                                    setErrors({...errors, gdpr: ''});
                                                }
                                            }}
                                            required
                                        />
                                        <span className="checkmark"></span>
                                        <span className="gdpr-text">
                                            I agree to the processing of my personal data 
                                            according to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy</a>. *
                                        </span>
                                    </label>
                                    <label className="gdpr-checkbox optional">
                                        <input
                                            type="checkbox"
                                            name="marketingConsent"
                                            checked={marketingConsent}
                                            onChange={(e) => setMarketingConsent(e.target.checked)}
                                        />
                                        <span className="checkmark"></span>
                                        <span className="gdpr-text">
                                            I would like to receive news and offers from Ferritico.
                                        </span>
                                    </label>
                                    {errors.gdpr && <span className="error-message">{errors.gdpr}</span>}
                                </div>
                                
                                <div className="recaptcha-inline">
                                    <div ref={recaptchaRef}></div>
                                    {errors.recaptcha && <span className="error-message">{errors.recaptcha}</span>}
                                </div>
                            </div>
                            
                            <button 
                                type="submit" 
                                className={`contact-submit ${gdprConsent ? 'gdpr-validated' : 'gdpr-pending'}`} 
                                disabled={isSubmitting}
                                onClick={handleButtonClick}
                            >
                                <span className="button-content">
                                    {isSubmitting ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            Sending your message...
                                        </>
                                    ) : (
                                        <>
                                            Send Message
                                            <span className="button-arrow">→</span>
                                        </>
                                    )}
                                </span>
                            </button>
                            <p className="form-privacy">
                                <span className="privacy-icon">🔒</span>
                                Your data is transmitted securely and processed in accordance with GDPR.
                            </p>
                            
                            {submitStatus.submitted && (
                                <div className={`form-status ${submitStatus.success ? 'success' : 'error'}`}>
                                    {submitStatus.message}
                                </div>
                            )}
                        </form>
                    </div>
                    
                    <div className="contact-info">
                        <div className="contact-card">
                            <div className="card-header">
                                <h3>Contact</h3>
                            </div>
                            
                            <div className="contact-methods-compact">
                                <div className="contact-method-compact">
                                    <span className="method-icon-compact">✉️</span>
                                    <div className="method-details">
                                        <strong>Email Us</strong><br/>
                                        <span>Direct line to our team</span><br/>
                                        <a href="mailto:contact@ferritico.com" className="contact-link">
                                            contact@ferritico.com
                                        </a>
                                    </div>
                                </div>

                                <div className="contact-method-compact">
                                    <span className="method-icon-compact linkedin-logo">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                        </svg>
                                    </span>
                                    <div className="method-details">
                                        <strong>Connect on LinkedIn</strong><br/>
                                        <span>Follow our updates</span><br/>
                                        <a
                                            href="https://linkedin.com/company/ferritico"
                                            className="contact-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Follow us →
                                        </a>
                                    </div>
                                </div>
                                

                                <div className="contact-method-compact">
                                    <span className="method-icon-compact">📞</span>
                                    <div className="method-details">
                                        <strong>Call Us</strong><br/>
                                        <span>Speak with our experts</span><br/>
                                        <a href="tel:+46812153003" className="contact-link">
                                            +46 (0)8 121 530 03
                                        </a>
                                    </div>
                                </div>

                            </div>
                            
                            <div className="office-info">
                                <h4>🏢 Our Office</h4>
                                <div className="office-address">
                                    <p>
                                        <strong>Ferritico AB</strong><br/>
                                        c/o Openlab<br/>
                                        Valhallav&auml;gen 79<br/>
                                        114 28 Stockholm, Sweden
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
            </div>
        </section>
    );
};

export default ContactSection;
